import React from 'react'
import { zipObj, update } from 'ramda'
import intl from 'react-intl-universal'

import { Website } from '~/types'

function getSitesAvailable(sitesObject, websitesConfiguration) {
  const usedKeys = zipObj(
    websitesConfiguration.map((c) => c.id),
    new Array(websitesConfiguration.length)
  )
  return Object.values(Object.assign({}, sitesObject, usedKeys))
    .filter((site) => !!site)
    .map((site) => site || {})
}

interface SiteServiceBasePath {
  default: boolean
  id: string
  path: string
}

interface Props {
  companySettings?: {
    siteServiceBasePathsV2?: SiteServiceBasePath[]
  }
  sites?: {
    [siteName: string]: Website
  }
  loadError?: {
    statusText: string
  }
  applyChanges: (basePaths: SiteServiceBasePath[]) => void
}

export default function SettingsSiteSelector(props: Props) {
  if (props.loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        <strong>
          {intl.get('SettingsSiteSelector.error')} -{' '}
          {props.loadError.statusText}.
        </strong>{' '}
        {intl.get('SettingsSiteSelector.errorMsg')}
      </div>
    )
  }

  if (!props.companySettings || !props.sites) {
    return (
      <div className="alert alert-info" role="alert">
        {intl.get('SettingsSiteSelector.loading')}
      </div>
    )
  }

  const websitesConfiguration =
    props.companySettings.siteServiceBasePathsV2 || []

  const sitesAvailable = getSitesAvailable(
    props.sites,
    websitesConfiguration
  ) as Website[]

  const onAddWebsite = (id: string) => {
    const siteItem = props.sites && props.sites[id]

    if (!id || !siteItem) {
      return
    }

    const withAddedItem = [
      ...websitesConfiguration,
      {
        id,
        path: siteItem.base_path || '',
        default: websitesConfiguration.length === 0,
      },
    ]
    props.applyChanges(withAddedItem)
  }

  const onDefault = (id: string) => {
    const configurationId = websitesConfiguration.findIndex(
      (item) => item.id === id
    )

    if (configurationId === -1) {
      return
    }

    const withUpdatedDefault = websitesConfiguration.map((item) => {
      item.default = item.id === id
      return item
    })
    props.applyChanges(withUpdatedDefault)
  }

  const onPathChange = (id: string, path: string) => {
    const configurationId = websitesConfiguration.findIndex(
      (item) => item.id === id
    )

    if (configurationId === -1) {
      return
    }

    const withUpdatedPath = update(
      configurationId,
      {
        ...websitesConfiguration[configurationId],
        path,
      },
      websitesConfiguration
    )
    props.applyChanges(withUpdatedPath)
  }

  const onRemove = (id: string) => {
    const withRemovedItem = websitesConfiguration.filter(
      (item) => item.id !== id
    )
    const hasDefault = withRemovedItem.findIndex((item) => item.default)
    if (withRemovedItem.length > 0 && !~hasDefault) {
      withRemovedItem[0].default = true
    }
    props.applyChanges(withRemovedItem)
  }

  return (
    <form>
      <div className="form-group">
        <label htmlFor="settings-site-selector">
          {intl.get('SettingsSiteSelector.addWebsite')}
        </label>
        {/* eslint-disable-next-line jsx-a11y/no-onchange */}
        <select
          id="settings-site-selector"
          className="col-sm-4 col-lg-2 ml-2"
          onChange={(event) => {
            const id = event.target.value
            onAddWebsite(id)
          }}
          value="DEFAULT"
        >
          <option value="DEFAULT" disabled>
            {intl.get('SettingsSiteSelector.defaultAddWebsite')}
          </option>
          {sitesAvailable.map((site) => (
            <option value={site._id} key={site._id}>
              {site.display_name}
            </option>
          ))}
        </select>
      </div>

      {websitesConfiguration.length > 0 && (
        <div className="form-group form-inline">
          <span className="col-sm-1">
            {intl.get('SettingsSiteSelector.default')}?
          </span>
          <span className="ml-sm-2 col-sm-3">
            {intl.get('SettingsSiteSelector.website')}
          </span>
          <span className="mb-2 mr-sm-2 mb-sm-0 col-sm-5">
            {intl.get('SettingsSiteSelector.path')}
          </span>
        </div>
      )}

      {websitesConfiguration.map((row) => (
        <div key={row.id} className="form-group form-inline">
          <div className="col-sm-1">
            <input
              data-testid={`radio-${row.id}`}
              className="form-control ml-3"
              type="radio"
              name="default"
              checked={row.default}
              onChange={() => onDefault(row.id)}
            />
          </div>
          <strong className="ml-sm-2 col-sm-3">{row.id}</strong>
          <input
            type="text"
            className="form-control mb-2 mr-sm-2 mb-sm-0 col-sm-5"
            placeholder={intl.get('SettingsSiteSelector.addPathPlaceholder')}
            defaultValue={row.path}
            onBlur={(e) => onPathChange(row.id, e.target.value)}
          />
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <i
            className="btn fa fa-trash ml-2 c-brand-danger"
            onClick={() => onRemove(row.id)}
            title="Remove Path"
          />
        </div>
      ))}
    </form>
  )
}
